import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { jobLogsResult, jobSnapshot, wsChannel } from '@xdocs/schemas';
import type { ErrorAlert, JobLog, JobSnapshot } from '@xdocs/schemas';
import { useWs } from '../../ws/WsProvider.js';

const TERMINAL_STATUSES = new Set(['succeeded', 'failed', 'canceled']);
const EARLIER_LOGS_PAGE_SIZE = 50;

export interface UseJobSnapshotResult {
  snapshot: JobSnapshot | undefined;
  isLoading: boolean;
  isError: boolean;
  error: ErrorAlert | null;
  refetch: () => void;
  /** `recentLogs` (from the snapshot) plus any earlier pages loaded via
   * {@link loadEarlierLogs}, oldest-first, deduped by id. */
  logs: JobLog[];
  loadEarlierLogs: () => Promise<void>;
  isLoadingEarlierLogs: boolean;
  earlierLogsError: string | null;
  hasMoreEarlierLogs: boolean;
}

/**
 * Live job state (F14/E11 §0.2, E20 §2 reattach). Fetches the reattach
 * snapshot (`job.subscribe`, which also subscribes the caller server-side),
 * re-fetches on every `job:{jobId}` push ("signal, not payload" convention —
 * pushes carry no data, only that something changed) and on the client's own
 * `job-reattach` event (reconnect-after-drop or a fresh tab pickup of a
 * previously-tracked job, §5.10-2). Tracks `jobId` for E20 persistence while
 * non-terminal; untracks once the job reaches a terminal status.
 */
export function useJobSnapshot(jobId: string | null): UseJobSnapshotResult {
  const { wsClient, connectionState } = useWs();
  const queryClient = useQueryClient();
  const enabled = connectionState === 'connected' && jobId !== null;

  const query = useQuery<JobSnapshot, ErrorAlert>({
    queryKey: ['job', jobId],
    // `jobSnapshot.parse` and not the raw payload: `requestTyped` is
    // compile-time-only (no runtime parse), so every `timestamp` field
    // arrives as a raw ISO STRING despite the `Date` type — which threw
    // `RangeError: Invalid time value` inside `JobProgressCard`'s
    // `formatDate(log.createdAt)` the moment the first real worker log
    // landed (white-screening the page — no error boundary above it).
    // Parsing at this trust boundary coerces (`z.coerce.date()`) and
    // validates in one step.
    queryFn: async () => jobSnapshot.parse(await requestTyped(wsClient, 'job.subscribe', { jobId: jobId as string })),
    enabled,
  });

  useEffect(() => {
    if (!jobId) return undefined;
    wsClient.trackJob(jobId);
    return undefined;
  }, [wsClient, jobId]);

  useEffect(() => {
    const status = query.data?.job.status;
    if (jobId && status && TERMINAL_STATUSES.has(status)) {
      wsClient.untrackJob(jobId);
    }
  }, [wsClient, jobId, query.data?.job.status]);

  useEffect(() => {
    if (!jobId) return undefined;
    const channel = wsChannel.job(jobId);
    return wsClient.subscribe(channel, () => {
      void queryClient.invalidateQueries({ queryKey: ['job', jobId] });
    });
  }, [wsClient, queryClient, jobId]);

  useEffect(() => {
    return wsClient.on('job-reattach', (reattachedId) => {
      if (reattachedId === jobId) void queryClient.invalidateQueries({ queryKey: ['job', jobId] });
    });
  }, [wsClient, queryClient, jobId]);

  const [earlierLogs, setEarlierLogs] = useState<JobLog[]>([]);
  const [nextEarlierPage, setNextEarlierPage] = useState(1);
  const [isLoadingEarlierLogs, setIsLoadingEarlierLogs] = useState(false);
  const [earlierLogsError, setEarlierLogsError] = useState<string | null>(null);
  const [hasMoreEarlierLogs, setHasMoreEarlierLogs] = useState(true);

  // Reset earlier-logs pagination whenever the job identity changes.
  useEffect(() => {
    setEarlierLogs([]);
    setNextEarlierPage(1);
    setEarlierLogsError(null);
    setHasMoreEarlierLogs(true);
  }, [jobId]);

  const loadEarlierLogs = useCallback(async (): Promise<void> => {
    if (!jobId || isLoadingEarlierLogs || !hasMoreEarlierLogs) return;
    setIsLoadingEarlierLogs(true);
    setEarlierLogsError(null);
    try {
      // Same runtime-coercion rationale as the `jobSnapshot.parse` above —
      // `page.rows[].createdAt` feeds `.getTime()` sorting below.
      const page = jobLogsResult.parse(
        await requestTyped(wsClient, 'job.logs', {
          jobId,
          page: nextEarlierPage,
          pageSize: EARLIER_LOGS_PAGE_SIZE,
          sort: 'createdAt',
          dir: 'desc',
        }),
      );
      setEarlierLogs((prev) => [...page.rows.slice().reverse(), ...prev]);
      setNextEarlierPage((p) => p + 1);
      setHasMoreEarlierLogs(page.rows.length === EARLIER_LOGS_PAGE_SIZE);
    } catch (err) {
      const alert = err as ErrorAlert;
      setEarlierLogsError(alert.message ?? 'jobs:logs.loadMoreError');
    } finally {
      setIsLoadingEarlierLogs(false);
    }
  }, [wsClient, jobId, nextEarlierPage, isLoadingEarlierLogs, hasMoreEarlierLogs]);

  const recent = query.data?.recentLogs ?? [];
  const seen = new Set<string>();
  const logs: JobLog[] = [];
  for (const log of [...earlierLogs, ...recent]) {
    if (seen.has(log.id)) continue;
    seen.add(log.id);
    logs.push(log);
  }
  logs.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());

  return {
    snapshot: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.isError ? query.error : null,
    refetch: () => void query.refetch(),
    logs,
    loadEarlierLogs,
    isLoadingEarlierLogs,
    earlierLogsError,
    hasMoreEarlierLogs,
  };
}
